import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import React from 'react'
import styled from '../../constants/theme'
import { capitalizeFirstLetter } from '../../lib/string'
import { SelectorIcon } from '../elements/Icons'
import PillButton from '../elements/PillButton'
import { isValidWhenNullOrFalse } from '../forms/FormCollector'
import FormElementFactory from './FormElementFactory'
import {
  Bar,
  BasicType,
  bottomBorderForType,
  ElementState,
  ElementType,
  FormElementProps,
  FormElementState,
  Label,
  QuestionType,
  RightButtonContainer,
  RightIconContainer,
} from './FormElementParts'

interface Cancelable {
  cancel(): void
}

interface ElementWrapperProps {
  display?: string
}

class FormElement extends React.Component<FormElementProps, FormElementState> {
  static defaultProps: Partial<FormElementProps> = {
    description: '',
    elementState: ElementState.INDETERMINATE,
    errorText: '',
    includeBlank: undefined,
    options: [],
    requiresPassword: false,
    value: '',
  }

  static contextTypes = {
    onFormElementChange: PropTypes.func,
  }

  state = {
    elementState: this.props.elementState || ElementState.INDETERMINATE,
    serverErrors: this.props.serverErrors,
    value: this.props.value || '',
  }

  debouncedInvalid: (() => void) & Cancelable

  constructor(props) {
    super(props)
    this.debouncedInvalid = debounce(this.setInvalid, 1500)
  }

  componentDidMount() {
    const { isRequired, name, requiresPassword, value } = this.props
    const { onFormElementChange } = this.context
    if (onFormElementChange && !requiresPassword) {
      this.validate()
      onFormElementChange(name, value, isRequired, this.props.validations)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.serverErrors !== this.props.serverErrors) {
      this.setState({ serverErrors: this.props.serverErrors })
    }
    if (
      prevProps.value !== this.props.value &&
      prevProps.value === this.state.value
    ) {
      this.setState({ value: this.props.value }, () => {
        this.validate()
      })
    }
  }

  validate(): boolean {
    let valid = true
    let clientValid = true
    let serverValid = true
    const { elementState, isRequired, validations } = this.props
    const { serverErrors, value } = this.state
    if (
      elementState === ElementState.INDETERMINATE ||
      isValidWhenNullOrFalse(isRequired, value)
    ) {
      clientValid = true
    } else if (validations && validations.length) {
      clientValid = validations.every(v => v(this.state.value))
    }
    if (serverErrors && serverErrors[this.props.name || '']) {
      serverValid = false
    }
    valid = clientValid && serverValid
    if (valid) {
      this.debouncedInvalid.cancel()
      this.setState({ elementState: ElementState.VALID })
    } else {
      if (this.state.value !== this.props.value) {
        this.debouncedInvalid()
      } else {
        this.debouncedInvalid.cancel()
        this.setState({
          elementState: this.props.elementState || ElementState.INDETERMINATE,
        })
      }
    }
    return valid
  }

  setInvalid() {
    this.setState({ elementState: ElementState.INVALID })
  }

  handleChange = ({ name, value }: { name: string; value: string }) => {
    const newErrors = this.state.serverErrors
    if (newErrors) {
      delete newErrors[name]
    }
    this.setState({ value, serverErrors: newErrors }, () => {
      const { isRequired, onChange } = this.props
      const { onFormElementChange } = this.context
      if (onChange) {
        onChange({ [name]: value })
      }
      if (onFormElementChange) {
        this.validate()
        onFormElementChange(name, value, isRequired, this.props.validations)
      }
    })
  }

  validClass(state: ElementState): string {
    return state.toLowerCase()
  }

  render() {
    const {
      description,
      disabled,
      display,
      errorText,
      icon,
      id,
      label,
      name,
      onEdit,
      requiresPassword,
      rightButton,
      rightIcon,
      showCharsRemaining,
      type,
    } = this.props
    const { elementState, serverErrors, value } = this.state
    const hasSeverError = serverErrors && serverErrors[name || '']
    const showClientsideError =
      !hasSeverError && elementState === ElementState.INVALID
    let unifiedErrorText = ''
    if (showClientsideError && errorText && errorText.length > 0) {
      unifiedErrorText = errorText
    } else if (serverErrors && serverErrors[name || '']) {
      unifiedErrorText = serverErrors[name || '']
    }
    const charsRemaining =
      (showCharsRemaining && showCharsRemaining - value.length) || 0
    const elementWrapperClassName =
      capitalizeFirstLetter(type) + `Element ${name}`
    const elementWrapperId = id || undefined
    return (
      <ElementWrapper
        className={elementWrapperClassName}
        id={elementWrapperId}
        display={display}
      >
        <InnerDiv>
          <InputContainer
            className={`${this.validClass(elementState)}`}
            icon={icon}
            onClick={requiresPassword ? onEdit : undefined}
            rightIcon={rightIcon}
            type={type}
          >
            {icon && (
              <IconContainer elementState={elementState}>{icon}</IconContainer>
            )}
            <FormElementFactory
              {...this.props}
              elementState={elementState}
              disabled={disabled || requiresPassword}
              onChange={this.handleChange}
              unifiedErrorText={unifiedErrorText}
              value={value || ''}
            />
            {type !== ElementType.SWITCH &&
              type !== ElementType.CHECKBOX &&
              type !== ElementType.RADIO && (
                <React.Fragment>
                  <Bar />
                  <Label
                    htmlFor={name}
                    icon={icon}
                    dangerouslySetInnerHTML={{
                      __html: label,
                    }}
                  />
                </React.Fragment>
              )}
            {rightButton && (
              <RightButtonContainer>{rightButton}</RightButtonContainer>
            )}
            {rightIcon && <RightIconContainer>{rightIcon}</RightIconContainer>}
            {!rightIcon && type === ElementType.SELECT && (
              <RightIconContainer>
                <SelectorIcon />
              </RightIconContainer>
            )}
          </InputContainer>
          {requiresPassword && !disabled && (
            <PillButton
              aria-label={`Change ${name}`}
              className="asGrey asOutline"
              onClick={onEdit}
            >
              Change
            </PillButton>
          )}
        </InnerDiv>
        {!!(
          showCharsRemaining &&
          (type === ElementType.TEXT || type === ElementType.TEXTAREA)
        ) && (
          <CharsRemaining
            className={charsRemaining >= 0 ? '' : 'invalid'}
          >{`${charsRemaining} characters remaining`}</CharsRemaining>
        )}
        {description && description.length > 0 && (
          <Description
            className="description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        {unifiedErrorText.length > 0 && (
          <ErrorDiv id={`${name}Error`}>{unifiedErrorText}</ErrorDiv>
        )}
      </ElementWrapper>
    )
  }
}

const Description = styled.div({
  '& a': {
    textDecoration: 'underline',
  },
  fontSize: 13,
  paddingBottom: 10,
})

const ElementWrapper = styled.div(
  {
    marginBottom: 5,
  },
  ({ display }: ElementWrapperProps) => (display === 'none' ? { display } : {}),
)

const InnerDiv = styled.div({
  '& .asOutline': {
    marginLeft: 10,
    marginTop: 22,
  },
  display: 'flex',
  position: 'relative',
})

const IconContainer = styled.div<{ elementState: ElementState }>(
  {
    left: -5,
    pointerEvents: 'none',
    position: 'absolute',
    top: -5,
    transform: 'scale(0.7)',
  },
  ({ elementState, theme }) => ({
    fill: elementState === ElementState.INVALID ? theme.error : theme.text,
  }),
)

const CharsRemaining = styled.div(
  {
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  ({ theme }) => ({
    '&.invalid': {
      color: theme.error,
    },
  }),
)

const ErrorDiv = styled.div(
  {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 5,
    minHeight: 20,
  },
  ({ theme }) => ({
    color: theme.error,
  }),
)

export interface InputContainerProps {
  icon?: JSX.Element
  onClick?: any
  rightIcon?: JSX.Element
  type: BasicType | ElementType | QuestionType
}

const InputContainer = styled.div<InputContainerProps>(
  {
    '& input:focus, & input[type="radio"]:focus ~ label, & input[class^="switch"]:focus ~ label, & select:focus, & textarea:focus': {
      fontWeight: 'bold',
    },
    '& input:focus:not([class^="switch"]) ~ span:first-of-type:before, & input:focus:not([class^="switch"]) ~ span:first-of-type:after': {
      width: '50%',
    },
    '& select:focus ~ span:first-of-type:before, & select:focus ~ span:first-of-type:after': {
      width: '50%',
    },
    '& textarea:focus ~ span:first-of-type:before, & textarea:focus ~ span:first-of-type:after': {
      width: '50%',
    },
    position: 'relative',
    width: '100%',
  },
  ({ icon, rightIcon, type, theme }) => ({
    'input[type="checkbox"]:checked ~ div[class^="box"]': {
      background: theme.primaryDark,
    },
    '& > input, & > select, & > textarea': {
      paddingLeft: icon ? 30 : 0,
      paddingRight: rightIcon || type === ElementType.SELECT ? 30 : 0,
    },
    '&.invalid': {
      borderBottom:
        type !== ElementType.SWITCH && type !== ElementType.CHECKBOX
          ? `2px solid ${theme.error}`
          : 'none',
    },
    borderBottom: `${bottomBorderForType(type, theme)}`,
    marginBottom:
      type !== ElementType.SWITCH && type !== ElementType.CHECKBOX ? 5 : 0,
    marginTop:
      type !== ElementType.SWITCH && type !== ElementType.CHECKBOX ? 20 : 0,
    transition: `border ${theme.speed} ${theme.ease}`,
  }),
)

export default FormElement
